import { z } from 'zod';
import { zLocale, THEMES } from './common';
import { CAPTCHA_PURPOSES, MAX_NONCE } from './captcha';

/**
 * Email: trimmed + lower-cased BEFORE validation, so the server stores and compares one canonical form
 * (no parser differential between register/login; `A@x.com` and `a@x.com` are the same account).
 */
export const zEmail = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.string().email().max(254));

/**
 * Nickname: NFC-normalized + trimmed, then 2..24 letters/digits/`_`/`.`/`-` (any script — de/zh/ru nicks allowed).
 * Uniqueness on the server is case-insensitive.
 */
export const NICKNAME_RE = /^[\p{L}\p{N}_.-]+$/u;
export const zNickname = z
  .string()
  .transform((s) => s.normalize('NFC').trim())
  .pipe(z.string().min(2).max(24).regex(NICKNAME_RE));

/** Avatar id (preset avatar key, e.g. `initial`, `a01`); never a URL or free text. */
export const AVATAR_RE = /^[a-z][a-z0-9-]{0,31}$/;
export const zAvatar = z.string().regex(AVATAR_RE);
export const DEFAULT_AVATAR = 'initial';

export const zPassword = z.string().min(8).max(128);

/** A solved proof-of-work captcha (see ./captcha.ts): the signed challenge + `k` distinct nonces. */
export const zCaptchaSolution = z.object({
  challenge: z.string().min(3).max(512),
  nonces: z.array(z.number().int().min(0).max(MAX_NONCE)).min(1).max(64),
});
export type CaptchaSolution = z.infer<typeof zCaptchaSolution>;

/** POST /api/auth/captcha — `email` only for login/password (the difficulty depends on that account's failures). */
export const zCaptchaRequest = z.object({ purpose: z.enum(CAPTCHA_PURPOSES), email: zEmail.optional() }).strict();
export type CaptchaRequest = z.input<typeof zCaptchaRequest>;
/** POST /api/auth/captcha response */
export interface CaptchaChallenge {
  /** signed token `<payload>.<hmac>`; the payload is readable with `decodeChallenge` */
  challenge: string;
  bits: number;
  count: number;
  expiresAt: string;
}

export const zRegister = z.object({
  email: zEmail,
  password: zPassword,
  /** optional: the design's sign-up form has no nickname; the server derives one from the email if omitted */
  nickname: zNickname.optional(),
  locale: zLocale,
  /** required unless the server has the captcha switched off (config.captcha.register.bits = 0) */
  captcha: zCaptchaSolution.optional(),
});
export type RegisterInput = z.input<typeof zRegister>;

/** `captcha` — only after CAPTCHA_REQUIRED (too many failed attempts for this account / IP) */
export const zLogin = z.object({ email: zEmail, password: z.string().min(1).max(128), captcha: zCaptchaSolution.optional() });
export type LoginInput = z.input<typeof zLogin>;
/** POST /api/auth/login response */
export interface LoginResult {
  me: Me;
  /** signing in cancelled a pending account deletion */
  deletionCancelled?: true;
}

/** POST /api/auth/logout-all — no parameters (an empty JSON body is allowed) */
export const zLogoutAll = z.object({}).strict();
/** POST /api/auth/logout-all response: every session of the user is deleted, the current one included */
export interface LogoutAllResult {
  ok: true;
  /** how many sessions were ended */
  sessions: number;
}

/**
 * POST /api/auth/password — change the password of the signed-in account.
 * `current` is checked like a login password; `next` follows the sign-up rules (`zPassword`).
 */
export const zChangePassword = z
  .object({ current: z.string().min(1).max(128), next: zPassword, captcha: zCaptchaSolution.optional() })
  .strict();
export type ChangePasswordInput = z.input<typeof zChangePassword>;
/** POST /api/auth/password response: the current session stays, every other session of the account is ended */
export interface ChangePasswordResult {
  ok: true;
  /** how many other sessions (devices) were signed out */
  sessionsEnded: number;
}

/**
 * POST /api/auth/password/set — an account without a password (Google / Apple / Telegram sign-up) sets one.
 * No current password; instead the session must be fresh: signed in (or confirmed through a provider, `mode=reauth`)
 * within REAUTH_WINDOW_MS, else REAUTH_REQUIRED. Needs an e-mail on the account (the password signs in with it).
 */
export const zSetPassword = z.object({ password: zPassword }).strict();
export type SetPasswordInput = z.input<typeof zSetPassword>;
/** POST /api/auth/password/set response */
export interface SetPasswordResult {
  me: Me;
}
/** how long a sign-in / provider confirmation counts as fresh for setting a password or deleting the account */
export const REAUTH_WINDOW_MS = 10 * 60_000;

export const zSettings = z
  .object({
    locale: zLocale.optional(),
    theme: z.enum(THEMES).optional(),
    sound: z.boolean().optional(),
    music: z.boolean().optional(),
    reducedMotion: z.boolean().optional(),
    nickname: zNickname.optional(),
    avatar: zAvatar.optional(),
  })
  .strict();
export type SettingsInput = z.input<typeof zSettings>;

export interface Me {
  id: string;
  /** short public number shown in the profile ("ID 20471") */
  publicId: number;
  /** null: the account was created with a provider that gives no e-mail (Telegram) and none was added yet */
  email: string | null;
  /** the account has a password (false: signs in with Google / Apple / Telegram only) */
  hasPassword: boolean;
  /** the e-mail address is confirmed (link from the confirmation letter) */
  emailVerified: boolean;
  nickname: string;
  avatar: string;
  role: 'user' | 'admin';
  balance: number;
  locale: z.infer<typeof zLocale>;
  theme: (typeof THEMES)[number];
  sound: boolean;
  music: boolean;
  reducedMotion: boolean;
  level: number;
  xp: number;
  streak: number;
  createdAt: string;
  /** a pending account deletion (7 days to cancel); null / absent = none */
  deletion?: AccountDeletion | null;
}

/* ---------------- account deletion (docs/superpowers/specs/2026-09-30-account-deletion-design.md) ---------------- */

/** Days between the request and the final erase; signing in or "Cancel" in the profile undoes the request. */
export const DELETION_GRACE_DAYS = 7;
/**
 * Nickname of an erased account (stored in the database). Spaces are not allowed in NICKNAME_RE, so no living
 * player can have it; clients show `common.deletedPlayer` instead (`isDeletedNickname`).
 */
export const DELETED_NICKNAME = 'Deleted player';
export const isDeletedNickname = (n: string | null | undefined): boolean => n === DELETED_NICKNAME;

export interface AccountDeletion {
  requestedAt: string;
  /** the account is erased at (or shortly after) this moment unless the request is cancelled */
  deleteAfter: string;
}

/**
 * POST /api/me/deletion — request the deletion of the signed-in account. `password` is checked like a login
 * (shared failure counters; `captcha` with purpose `password` after CAPTCHA_REQUIRED), `confirm` must be the
 * player's own nickname (case-insensitive). An account without a password (Google / Apple / Telegram only)
 * sends no `password`: the session and the typed nickname confirm it.
 */
export const zDeletionRequest = z
  .object({ password: z.string().min(1).max(128).optional(), confirm: z.string().max(64), captcha: zCaptchaSolution.optional() })
  .strict();
export type DeletionRequestInput = z.input<typeof zDeletionRequest>;
/** POST /api/me/deletion and POST /api/me/deletion/cancel response */
export interface DeletionResult {
  me: Me;
}

/** POST /api/auth/verify-email — the token from the confirmation link `/<lang>/verify?token=…` */
export const zVerifyEmail = z.object({ token: z.string().min(16).max(128) }).strict();
export type VerifyEmailInput = z.input<typeof zVerifyEmail>;
export interface VerifyEmailResult {
  ok: true;
}
/** POST /api/auth/verify-email/resend response */
export interface ResendVerificationResult {
  ok: true;
  /** the address was already confirmed: nothing was sent */
  alreadyVerified: boolean;
}

export interface DailyBonusState {
  /** 1..7 — which day of the 7-day calendar is claimable/next (if already claimed today: the day claimed today) */
  day: number;
  claimableNow: boolean;
  /** rewards for days 1..7 */
  rewards: number[];
  /** consecutive days claimed so far (design: "Day 4 of 7 · streak 3 days") */
  streak: number;
  /** when the next claim becomes possible (UTC midnight); null if claimable now */
  nextClaimAt: string | null;
}

/** POST /api/bonus/daily/claim response */
export interface DailyBonusClaimResult {
  amount: number;
  balance: number;
  state: DailyBonusState;
}
